import React from "react";
import { ShieldAlert } from "lucide-react";
import ReportShell from "@/components/ReportShell";
import { Field, TextInput, TextArea, Checkbox } from "@/components/ReportPrimitives";
import { buildHseIncidentReport, hseReviewChecklist, HSE_QUICK_PHRASES, HSE_REMINDER } from "@/lib/reports/hseIncident";
import { hseStore } from "@/lib/reports/storage";
import { useReportRceWiring } from "@/lib/rce/useReportRceWiring";

const emptyState = () => ({
  id: null, header: {}, details: {}, people: {}, actions: {}, evidence: {}, suspected: {}, followUp: { required: false },
});

function QuickPhrases({ onPick, testId }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {HSE_QUICK_PHRASES.map((p) => (
        <button key={p} type="button" onClick={() => onPick(p)} className="text-[10.5px] px-2 py-1 rounded-md" style={{ background: "rgba(22,24,29,0.6)", color: "var(--rs-text-2)", border: "1px solid var(--rs-border)" }}>+ {p}</button>
      ))}
    </div>
  );
}

const HseReminderBanner = ({ testId }) => (
  <div
    data-testid={testId}
    className="card-rs p-3 flex items-start gap-2.5"
    style={{ background: "rgba(244,67,54,0.07)", borderColor: "#C62828" }}
  >
    <ShieldAlert className="w-4 h-4 mt-[2px] shrink-0" style={{ color: "var(--rs-danger)" }} />
    <p className="text-[11px] leading-snug text-[#FCA5A5]">
      <span className="font-semibold" style={{ color: "var(--rs-danger)" }}>Regulatory notification reminder.</span>{" "}
      {HSE_REMINDER}
    </p>
  </div>
);

export default function HseIncidentForm() {
  const rce = useReportRceWiring("hseIncidentForm");
  const sections = [
    { id: "header", badge: "01", title: "Report Header", defaultOpen: true, render: (state, set) => (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Report Date"><TextInput type="date" value={state.header.reportDate} onChange={(v) => set("header","reportDate",v)} testId="hse-h-reportDate" /></Field>
        <Field label="Rig Name"><TextInput value={state.header.rigName} onChange={(v) => set("header","rigName",v)} testId="hse-h-rigName" /></Field>
        <Field label="Well Name"><TextInput value={state.header.wellName} onChange={(v) => set("header","wellName",v)} testId="hse-h-wellName" /></Field>
        <Field label="Operator / Client"><TextInput value={state.header.operator} onChange={(v) => set("header","operator",v)} testId="hse-h-operator" /></Field>
        <Field label="Prepared By"><TextInput value={state.header.preparedBy} onChange={(v) => set("header","preparedBy",v)} testId="hse-h-preparedBy" /></Field>
        <Field label="Position"><TextInput value={state.header.position} onChange={(v) => set("header","position",v)} testId="hse-h-position" /></Field>
        <Field
          label="Event Type (as you enter it)"
          helper="Free-text only. This tool does not classify severity, incident class, or injury grade."
        >
          <TextInput value={state.header.eventType} onChange={(v) => set("header","eventType",v)} placeholder="e.g. near miss, observation, first-aid, spill (user-entered)" testId="hse-h-eventType" />
        </Field>
      </div>
    )},
    { id: "details", badge: "02", title: "Event Details", render: (state, set) => (
      <>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Date / Time of Event"><TextInput type="datetime-local" value={state.details.dateTime} onChange={(v) => set("details","dateTime",v)} testId="hse-d-dateTime" /></Field>
          <Field label="Location"><TextInput value={state.details.location} onChange={(v) => set("details","location",v)} testId="hse-d-location" /></Field>
          <Field label="Activity in Progress"><TextInput value={state.details.activity} onChange={(v) => set("details","activity",v)} testId="hse-d-activity" /></Field>
        </div>
        <Field label="Brief Description (factual, neutral)" helper="Record facts only. Avoid blame language; obvious blame phrases are flagged in the polished draft.">
          <TextArea value={state.details.description} onChange={(v) => set("details","description",v)} rows={4} testId="hse-d-description" />
        </Field>
      </>
    )},
    { id: "people", badge: "03", title: "Persons Involved", render: (state, set) => (
      <>
        <Field label="Persons Involved (text only)"><TextArea value={state.people.involved} onChange={(v) => set("people","involved",v)} rows={2} testId="hse-p-involved" /></Field>
        <Field label="Witnesses (text only)"><TextArea value={state.people.witnesses} onChange={(v) => set("people","witnesses",v)} rows={2} testId="hse-p-witnesses" /></Field>
        <p className="text-[10px] text-[var(--rs-text-3)] leading-snug">Names recorded for company internal reporting only. No blame inferred by this tool.</p>
      </>
    )},
    { id: "actions", badge: "04", title: "Immediate Actions", render: (state, set, helpers) => (
      <>
        <Field label="Immediate Actions Taken"><TextArea value={state.actions.immediate} onChange={(v) => set("actions","immediate",v)} rows={3} testId="hse-a-immediate" /></Field>
        <Field label="Persons Informed"><TextArea value={state.actions.personsInformed} onChange={(v) => set("actions","personsInformed",v)} rows={2} testId="hse-a-personsInformed" /></Field>
        <QuickPhrases onPick={(p) => helpers.setState((s) => ({ ...s, actions: { ...s.actions, immediate: ((s.actions.immediate || "") + (s.actions.immediate && !/\s$/.test(s.actions.immediate || "") ? " " : "") + p) } }))} testId="hse-a-quick-phrases" />
      </>
    )},
    { id: "evidence", badge: "05", title: "Photos / Evidence Noted", render: (state, set) => (
      <Field label="Photos / Evidence Notes"><TextArea value={state.evidence.notes} onChange={(v) => set("evidence","notes",v)} rows={2} testId="hse-e-notes" /></Field>
    )},
    { id: "suspected", badge: "06", title: "Suspected / Under-Review Notes", render: (state, set) => (
      <>
        <p className="text-[10.5px] text-[var(--rs-text-3)] leading-snug">
          This tool does not assign severity, root cause, or incident class. If a possible cause is noted, prefix with "suspected" or "under review" — the polished draft preserves your exact wording.
        </p>
        <Field label="Notes (user-entered only)"><TextArea value={state.suspected.notes} onChange={(v) => set("suspected","notes",v)} rows={2} testId="hse-s-notes" /></Field>
      </>
    )},
    { id: "followUp", badge: "07", title: "Follow-Up Required", render: (state, set) => (
      <>
        <Checkbox checked={state.followUp.required} onChange={(v) => set("followUp","required",v)} label="Follow-Up Required" testId="hse-fu-required" />
        <Field label="Follow-Up Detail"><TextArea value={state.followUp.detail} onChange={(v) => set("followUp","detail",v)} rows={2} testId="hse-fu-detail" /></Field>
        <Field label="Company Procedure Reference (if known)"><TextInput value={state.followUp.companyProcedureRef} onChange={(v) => set("followUp","companyProcedureRef",v)} testId="hse-fu-companyProcedureRef" /></Field>
      </>
    )},
  ];

  return (
    <ReportShell
      testIdPrefix="hse"
      reportType="hseIncident"
      safetyCritical={true}
      title="HSE Incident / Near Miss Form"
      subtitle="Facts-only draft. This tool does not classify, grade, or blame."
      brandLine="HSE Incident / Near Miss Form  ·  Draft Documentation Support"
      emptyState={emptyState}
      buildFn={buildHseIncidentReport}
      checklistFn={hseReviewChecklist}
      store={hseStore}
      sections={sections}
      initialState={rce.initialState}
      rceContextStrip={rce.rceContextStrip}
      topNotice={<HseReminderBanner testId="hse-reminder-top" />}
      bottomNotice={<HseReminderBanner testId="hse-reminder-bottom" />}
      filenameFromState={(s) => {
        const safe = (v) => (v || "").toString().replace(/[^a-z0-9-_]+/gi, "_").slice(0, 30);
        return `RigSentinel_HSE_${safe(s.header.wellName) || "well"}_${(s.header.reportDate || "").replace(/[^0-9]/g, "") || Date.now()}.pdf`;
      }}
    />
  );
}
